import { type IFrontOfficeModule } from '@framework/common-web/frontoffice/module/IFrontOfficeModule.ts';
import { Alert } from 'antd';
import { useFormState } from 'react-hook-form';
import { SubmissionModuleLogic } from '../../../submission/submission-module/SubmissionModuleLogic.ts';

interface IFormIncoherenceAlertProps {
  module: IFrontOfficeModule;
}

/**
 * Alert che viene mostrato quando ci sono delle incoerenze sulla validazione del form.
 * Può accadere quando il frontend valida correttamente il form ma il backend trova
 * un errore (e.g. validazione serverside su valori da servizio).
 */
export function FormIncoherenceAlert(props: IFormIncoherenceAlertProps) {
  const { module } = props;

  // NON sottoscrivere `isValid` direttamente da formState: il proxy di RHF
  // marca la proprietà come "subscribed", forzando `_setValid → _runSchema`
  // (full resolver) su ogni `register` di ogni campo.
  // Deriviamo `isValid` da `errors` senza overhead aggiuntivo.
  // Siamo costretti a controllare `isSubmitted` perché al mount `errors` è `{}` (nessuna
  // validazione è ancora avvenuta), il che produrrebbe un falso "isValid = true"
  // mostrando il banner di incoerenza anche quando l'errore ESISTE lato frontend.
  const { errors, isSubmitted } = useFormState();
  const isValid = isSubmitted && Object.keys(errors).length === 0;

  // L'alert viene mostrato solo se:
  // - il modulo è stato visitato almeno una volta (e quindi salvato)
  // - lo stato di validazione del modulo è "NON VALIDO"
  // - il form è valido secondo la validazione client side (quindi l'errore è sicuramente dovuto a una incoerenza serverside)
  // Dobbiamo nascondere l'alert per i moduli che non sono mai stati salvati, dato che il default di statoValidazione.valido è a false
  // e non avrebbe senso mostrare un alert di incoerenza in questo caso.
  const isVisible =
    SubmissionModuleLogic.isVisited(module.statoValidazione) &&
    !SubmissionModuleLogic.isValid(module.statoValidazione) &&
    isValid;

  if (!isVisible) {
    return null;
  }

  return (
    <Alert
      closable
      type="error"
      message="Rilevata incoerenza di validazione"
      showIcon
      description="Sono state rilevate delle incoerenze durante la validazione remota. Si prega di controllare il modulo e riprovare. (Errore EVS-001)"
      style={{ marginBottom: 16 }}
    />
  );
}
